<template>
  <div>
    <wc-page-title title='Email Log'></wc-page-title>
    <v-container>
      <v-row>
        <v-col cols='12'>
          <wc-data-box
            title='Email Log'
            titleIcon='mdi-email-outline'
            :loading='isLoading'
            showDownloadCSV
            @downloadCSV='downloadCSV'
          >
            <template v-slot:middle_column>
              <v-row dense>
                <v-col cols='4'>
                  <wc-datepicker
                    label='From'
                    slim
                    v-model='dateFrom'
                    :max='dateTo'
                    @change='changeFilter'
                  ></wc-datepicker>
                </v-col>
                <v-col cols='4'>
                  <wc-datepicker
                    label='To'
                    slim
                    v-model='dateTo'
                    :min='dateFrom'
                    @change='changeFilter'
                  ></wc-datepicker>
                </v-col>
                <v-col cols='4'>
                  <v-select
                    :items='statusOptions'
                    label='Status'
                    dense
                    hide-details
                    v-model='status'
                    @change='changeFilter'
                  ></v-select>
                </v-col>
              </v-row>
            </template>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Description</th>
                    <th>Related Record</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for='row in rows' :key='row.id'>
                    <td>{{ row.action_date | shortDate }}</td>
                    <td>
                      <v-chip small :color='statusColor(row.status)' text-color='white'>
                        {{ statusLabel(row.status) }}
                      </v-chip>
                    </td>
                    <td>{{ row.description }}</td>
                    <td>
                      <v-btn v-if='row.client' text x-small :loading='loading' :disabled='loading' @click='openClient(row.client)'>
                        <v-icon small left color='info'>mdi-open-in-new</v-icon>
                        Client
                      </v-btn>
                      <v-btn v-if='row.estimate' text x-small :loading='loading' :disabled='loading' @click='openEstimate(row.estimate)'>
                        <v-icon small left color='info'>mdi-open-in-new</v-icon>
                        Estimate
                      </v-btn>
                      <v-btn v-if='row.invoice' text x-small :loading='loading' :disabled='loading' @click='openInvoice(row.invoice)'>
                        <v-icon small left color='info'>mdi-open-in-new</v-icon>
                        Invoice
                      </v-btn>
                      <v-btn v-if='row.project' text x-small :loading='loading' :disabled='loading' @click='openProject(row.project)'>
                        <v-icon small left color='info'>mdi-open-in-new</v-icon>
                        Project
                      </v-btn>
                      <v-btn v-if='row.work_order' text x-small :loading='loading' :disabled='loading' @click='openWorkOrder(row.work_order)'>
                        <v-icon small left color='info'>mdi-open-in-new</v-icon>
                        Work Order
                      </v-btn>
                      <v-btn v-if='row.subd_tracker' text x-small :loading='loading' :disabled='loading' @click='openSUBD(row.subd_tracker)'>
                        <v-icon small left color='info'>mdi-open-in-new</v-icon>
                        SUBD Tracker
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <div class='text-center' style='padding-top: 25px; padding-bottom: 25px;'>
              <v-pagination v-model='page' :length='paginatorLength' @input='changePage'></v-pagination>
            </div>
          </wc-data-box>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import moment from 'moment';
import trackerAPI from '@/api/tracker';
import estimatesAPI from '@/modules/estimates/api/estimates';
import projectsAPI from '@/modules/projects/api/projects';

export default {
  name: 'email-log',
  data: () => ({
    isLoading: false,
    loading: false,
    dateFrom: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    dateTo: moment().format('YYYY-MM-DD'),
    status: '',
    statusOptions: [
      { text: 'All', value: '' },
      { text: 'Delivered', value: 'delivered' },
      { text: 'Bounced', value: 'bounced' },
      { text: 'Opened', value: 'opened' },
      { text: 'Spam Complaint', value: 'spam_complaint' }
    ],
    rows: [],
    count: 0,
    page: 1,
    numberPerPage: 50
  }),
  computed: {
    paginatorLength () {
      return Math.max(1, Math.ceil(this.count / this.numberPerPage));
    }
  },
  mounted () {
    this.getData();
  },
  methods: {
    async getData () {
      this.isLoading = true;
      const data = await trackerAPI.getEmailLog(this.dateFrom, this.dateTo, this.status, this.page, this.numberPerPage);
      this.rows = data ? data.results : [];
      this.count = data ? data.count : 0;
      this.isLoading = false;
    },
    changeFilter () {
      this.page = 1;
      this.getData();
    },
    changePage () {
      this.getData();
    },
    statusLabel (status) {
      const found = this.statusOptions.find(o => o.value === status);
      return found ? found.text : 'Unknown';
    },
    statusColor (status) {
      if (status === 'bounced' || status === 'spam_complaint') { return 'error' }
      if (status === 'delivered') { return 'success' }
      if (status === 'opened') { return 'info' }
      return 'grey'
    },
    async downloadCSV () {
      const blob = await trackerAPI.downloadEmailLogCSV(this.dateFrom, this.dateTo, this.status);
      if (!blob) {
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `email_log_${this.dateFrom}_${this.dateTo}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    },
    async openEstimate (id) {
      this.loading = true;
      let tmp = await estimatesAPI.getEstimates(id);
      let division = tmp.division_obj.division;
      this.$router.push({ name: division + 'Estimate', params: { id: id }});
      this.loading = false;
    },
    async openProject (id) {
      this.loading = true;
      let tmp = await projectsAPI.getProject(id);
      let division = tmp.division_obj.division;
      this.$router.push({ name: division + 'Overview', params: { id: id }});
      this.loading = false;
    },
    openWorkOrder (id) {
      this.$router.push({ name: 'WOOverview', params: { id: id }});
    },
    openInvoice (id) {
      this.$router.push({ name: 'InvoiceOverview', params: { id: id }});
    },
    openClient (id) {
      this.$router.push({ name: 'ClientOverview', params: { id: id }});
    },
    openSUBD (id) {
      this.$router.push({ name: 'StartupDetails', params: { id: id }});
    }
  }
}
</script>
